import Room from './Room';
import App from './App';
import { Puzzle } from './Puzzle';

import {ClientV1 as Multiplayer} from '@javfres/escaperoom-multiplayer';
import sleep from '@/utils/sleep';
import Hint from './Hint';
import gaLog from '@/utils/ga';


type game_fn_t = () => Game;

type event_t = {
    type: string,
    data: string,
};

export default class Game {

    //
    // Static
    //
    private static current_game: Game|null = null;
    private static constructors: {[id:string]:game_fn_t} = {};
    private static default_name: string|null = null;

    static register(name:string, fn:game_fn_t){
        this.constructors[name] = fn;
    }

    static registerDefault(name:string, fn:game_fn_t){
        this.register(name, fn);
        this.default_name = name;
    }

    static new(name:string|null = null): Game {

        if(!name && !this.default_name){
            throw new Error('No game specified');
        }

        if(!name) name = this.default_name!;

        Room.current_id = 0;
        Puzzle.current_id = 0;
        Hint.current_id = 0;

        const fn = this.constructors[name];
        if(!fn){
            throw new Error(`There is no game named '${name}'`);
        }

        const game = fn();
        game.id = name;
        game.save();
        return game;
    }

    public static async current(): Promise<Game|null>{

        if(this.current_game) return this.current_game;

        // 
        if(App.localStorageAvailable()){
            const id = localStorage.getItem(App.id + '-id');
            const data = localStorage.getItem(App.id + '-data');
            if(id){
                const game = this.new(id);
                await game.fromData(data!);
                return game;
            }
        } else {
            console.warn("No local storage available");
        }

        return null;
    }




    //
    // Class
    //

    id: string = '';

    rooms: Room[] = [];
    current_room: Room|null = null;
    points: number = 0;
    start: Date = new Date();
    end: Date|null = null;

    events: event_t[] = [];

    multiplayer: Multiplayer|null = null;

    constructor(){
        Game.current_game = this;
    }

    add(elem: Room){

        if(elem instanceof Room){
            if(!this.current_room) this.current_room = elem;
            this.rooms.push(elem);
            return;
        }

    }

    changeRoom(room: Room){
        this.current_room = room;

        gaLog('room', {room_id:room.id, room_name:room.name});

    }


    toData(): string{

        let multiplayer_game_id = null;
        let my_user_data = null;
        if(this.multiplayer){
            multiplayer_game_id = this.multiplayer.game_id;
            const data = this.multiplayer.user_data[this.multiplayer.user_idx];
            let {_user_id, ...my_data} = data;
            my_user_data = my_data;
        }

        return JSON.stringify({
            multiplayer_game_id,
            my_user_data,
            events: this.events,
            start: this.start.getTime(),
            end: this.end ? this.end.getDate() : null,
            //points: this.points,
        });
    }

    async fromData(data: string){

        const obj = JSON.parse(data);

        this.start = new Date(obj.start);
        this.end = obj.end ? new Date(obj.end) : null;
        //this.points = obj.points;

        obj.events.map((event:event_t) => {
            this.addEvent(event.type, event.data, true);
        });
        
        if(obj.multiplayer_game_id){
            const multiplayer = new Multiplayer(App.SERVER);
            await multiplayer.join(obj.multiplayer_game_id);
            this.join(multiplayer);
            multiplayer.emitUserData(obj.my_user_data);
            this.save();
        }

    }


    processEvent(event:event_t, quiet:boolean){

        console.log("Process event", event);


        if(event.type === 'solved'){
            const elem = <Puzzle|null> this.getElement(event.data);
            if(!elem){
                console.error("Unable to find element " + event.data);
                return;
            }
            elem.solve(quiet);

        }

        if(event.type === 'useHint'){
            const elem = <Hint|null> this.getElement(event.data);
            if(!elem){
                console.error("Unable to find element " + event.data);
                return;
            }
            elem.use();

        }
        

    }


    save(){

        if(!App.localStorageAvailable()){
            console.warn("No local storage available");
            return;
        }

        console.log("Save", this.toData());

        localStorage.setItem(App.id + '-id', this.id);
        localStorage.setItem(App.id + '-data', this.toData());

    }

    addEvent(type:string, data:string, quiet:boolean=false, mp_user:string|null = null){

        const event = {type, data};

        console.log(event, this.events.map(x=>x));

        for(let e of this.events){
            if(e.type === type && e.data === data){
                console.log("Repeated event", event);
                return;  
            }
        }



        this.events.push(event);
        this.save();

        this.processEvent(event, quiet);

        
        (async () => {

            if(!this.multiplayer || mp_user) return;

            await sleep(50);
            this.multiplayer!.emitGameEvent(event);

        })();

    
    }

    getElement(id:string){

        for(let room of this.rooms){
            if(room.id === id) return room;
            const elem = room.getElement(id);
            if(elem) return elem; 
        }

        return null;
    }


    join(multiplayer: Multiplayer){

        console.log('Game.join');

        this.multiplayer = multiplayer;

        this.multiplayer.onGameEvent(async (mp_event, user_id) => {

            await sleep(50);

            console.log('>>>>', mp_event, 'from', user_id, 'me:', this.multiplayer?.user_id);
            this.addEvent(mp_event.type, mp_event.data, false, user_id);

        });


        // Reload the game events: Just in case you miss someone of join after the game has started
        console.log("Multilyer events: ", multiplayer.game_events);
        multiplayer.game_events.forEach(mp_event => {
            this.addEvent(mp_event.data.type, mp_event.data.data, true);
        });
        
        this.save();

    }


    finish(){
        this.end = new Date();
    }

    getElapsedMinutes(){

        const a = this.start.getTime();
        const b = (new Date()).getTime();

        return Math.floor((b-a) / (1000*60));
    }

}